import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchLeads,
  createLead,
  updateLead,
  deleteLead,
  patchLeadStatus,
} from "../../../store/leadsSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { createLearner } from "../../../store/learnersSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";
const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role || null;
const isReadOnly = userRole === "sales";

const Leads = () => {
  const dispatch = useDispatch();
  const { leads, loading } = useSelector((state) => state.leads);
  const { ictData } = useSelector((state) => state.ui);

  const [leadSearch, setLeadSearch] = useState("");
  const [ictSearch, setIctSearch] = useState("");
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadForm, setLeadForm] = useState({
    leadName: "",
    leadEmail: "",
    leadContact: "",
    briefRequirement: "",
    leadStatus: "new",
    ictId_id: "",
    trainingId: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchLeads());
    dispatch(fetchICTData());
  }, [dispatch]);

  const handleLeadSearch = (e) => setLeadSearch(e.target.value);
  const handleIctSearch = (e) => setIctSearch(e.target.value);

  const filteredLeads = leads.filter((lead) => {
    const ictName = ictData.find((ict) => ict.id === lead.ictId_id)?.ictName || "";
    return (
      lead.leadName.toLowerCase().includes(leadSearch.toLowerCase()) &&
      ictName.toLowerCase().includes(ictSearch.toLowerCase())
    );
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const displayedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOpenPopup = (lead = null) => {
    setEditingLead(lead);
    setLeadForm(
      lead || {
        leadName: "",
        leadEmail: "",
        leadContact: "",
        briefRequirement: "",
        leadStatus: "new",
        ictId_id: "",
        trainingId: "",
      }
    );
    setPopupOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const preparedForm = {
      ...leadForm,
      trainingId: leadForm.trainingId ? parseInt(leadForm.trainingId, 10) : null,
      ictId_id: leadForm.ictId_id ? parseInt(leadForm.ictId_id, 10) : null,
    };

    try {
      if (editingLead) {
        await dispatch(updateLead({ ...preparedForm, id: editingLead.id })).unwrap();
        toast.success("Lead updated successfully!");
      } else {
        await dispatch(createLead(preparedForm)).unwrap();
        toast.success("Lead created successfully!");
      }
      setPopupOpen(false);
      dispatch(fetchLeads());
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to process the request.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      try {
        await dispatch(deleteLead(id)).unwrap();
        toast.success("Lead deleted successfully!");
        dispatch(fetchLeads());
      } catch {
        toast.error("Failed to delete the lead.");
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await dispatch(patchLeadStatus({ id, leadStatus: newStatus })).unwrap();
      toast.success("Status updated successfully!");

      if (newStatus === "converted") {
        const lead = leads.find((lead) => lead.id === id);
        const learnerData = {
          firstName: lead.leadName || "",
          lastName: "",
          username: `${lead.leadName || ""}${lead.id}`,
          contact: lead.leadContact || "",
          email: lead.leadEmail || "",
          orgName: "Organization Name",
          countryName: "Country Name",
          status: "A",
          trainingId: lead.trainingId || null,
          ictId_id: lead.ictId_id || null,
          password: lead.leadContact || "default_password",
        };

        await dispatch(createLearner(learnerData)).unwrap();
        toast.success("Learner created successfully from the converted lead!");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status or create learner.");
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="container">
      <div className="header-container">
        <h3>Leads Management</h3>
        <button className="add-lead-btn" onClick={() => handleOpenPopup()}>
          <i className="fa fa-plus"></i>
        </button>
      </div>
      <div className="search-section_leads">
        <input
          type="text"
          placeholder="Search by Lead Name"
          value={leadSearch}
          onChange={handleLeadSearch}
          className="search-input"
        />
        <input
          type="text"
          placeholder="Search by ICT Name"
          value={ictSearch}
          onChange={handleIctSearch}
          className="search-input"
        />
      </div>
      {loading ? (
        <p>Loading leads...</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Lead Name</th>
                <th>Contact Details</th>
                <th>ICT Name</th>
                <th>Lead Status</th>
                <th>Requirement</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedLeads.map((lead, index) => (
                <tr key={lead.id}>
                  <td>
                    {lead.leadStatus === "new" && (
                      <i className="fa fa-bell" style={{ color: "red", marginLeft: "5px" }}></i>
                    )}
                    {lead.leadStatus === "contacted" && (
                      <i className="fa fa-phone" style={{ color: "green", marginLeft: "5px" }}></i>
                    )}
                    {lead.leadStatus === "converted" && (
                      <i className="fa fa-check-square" style={{color: "#ffc300", marginLeft: "5px" }}></i>
                    )}
                    {lead.leadStatus === "pending" && (
                      <i className="fa fa-clock" style={{color: "#999", marginLeft: "5px" }}></i>
                    )}
                    {(lead.leadStatus === "rejected" || lead.leadStatus === "cancelled") && (
                      <i className="fa fa-warning" style={{ color: "orange", marginLeft: "5px" }}></i>
                    )}
                    &nbsp;#
                    {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                  <td>
                    {lead.leadName}
                  </td>
                  <td>
                    {lead.leadEmail}
                    <br />
                    {lead.leadContact}
                  </td>
                  <td>{ictData.find((ict) => ict.id === lead.ictId_id)?.ictName || "N/A"}</td>
                  <td>
                    <select
                      value={lead.leadStatus}
                      onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                      disabled={lead.leadStatus === "converted"}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="converted">Converted</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td>{lead.briefRequirement}</td>
                  <td>
                    {lead.leadStatus !== "converted" && (
                      <>
                        <i
                          className="icon-btn update-btn fa fa-edit"
                          onClick={() => handleOpenPopup(lead)}
                        ></i>
                        &nbsp;
                        {!isReadOnly && (
                        <i
                          className="fa fa-trash icon-btn delete-btn"
                          onClick={() => handleDelete(lead.id)}
                        ></i>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                className={`pagination-btn ${currentPage === i + 1 ? "active" : ""}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </>
      )}
      {isPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editingLead ? "Edit Lead" : "Add Lead"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Lead Name</label>
                <input
                  type="text"
                  placeholder="Name"
                  value={leadForm.leadName}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, leadName: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Lead Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  value={leadForm.leadEmail}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, leadEmail: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Lead Contact</label>
                <input
                  type="text"
                  placeholder="Contact"
                  value={leadForm.leadContact}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, leadContact: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Lead Requirement</label>
                <textarea
                  placeholder="Requirement"
                  value={leadForm.briefRequirement}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, briefRequirement: e.target.value })
                  }
                ></textarea>
              </div>
              <div className="form-group">
                <label>Select Related ICT</label>
                <select
                  value={leadForm.ictId_id}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, ictId_id: parseInt(e.target.value, 10) })
                  }
                >
                  <option value="">Select ICT</option>
                  {ictData.map((ict) => (
                    <option key={ict.id} value={ict.id}>
                      {ict.ictName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Map Training Id</label>
                <input
                  type="number"
                  placeholder="Training ID"
                  value={leadForm.trainingId}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, trainingId: parseInt(e.target.value, 10) })
                  }
                />
              </div>
              <button type="submit" className="submit-btn">
                Save
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setPopupOpen(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default Leads;
